import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient from '../api/apiClient';
import config from '../utils/config';

// Create context
const AuthContext = createContext();

// Hook for using auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Login user
  const login = async (credentials) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Handle both direct login with email/password and login with token/user object
      let response;
      
      if (credentials.token && credentials.user) {
        // Login from token (for email verification flow)
        response = {
          token: credentials.token,
          data: { user: credentials.user }
        };
      } else {
        // Normal login with email and password
        response = await apiClient.post(
          config.AUTH_ENDPOINTS.LOGIN, 
          { 
            email: credentials.email, 
            password: credentials.password,
            accountType: credentials.accountType || 'main'
          }
        );
      }
      
      const { token, data } = response;
      
      // Debug: Log the exact response data
      console.log('AUTH CONTEXT LOGIN DEBUG - Full response:', response);
      console.log('AUTH CONTEXT LOGIN DEBUG - Token:', token);
      console.log('AUTH CONTEXT LOGIN DEBUG - Data:', data);
      console.log('AUTH CONTEXT LOGIN DEBUG - User object:', data.user);
      console.log('AUTH CONTEXT LOGIN DEBUG - isSubUser flag:', data.user?.isSubUser);
      
      // Store token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Debug: Log what was stored
      console.log('AUTH CONTEXT LOGIN DEBUG - Stored in localStorage:', {
        token: localStorage.getItem('token'),
        user: JSON.parse(localStorage.getItem('user'))
      });
      
      setCurrentUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Register user
  const register = async (userData) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiClient.post(
        config.AUTH_ENDPOINTS.SIGNUP, 
        userData
      );
      
      // With email verification, we don't log the user in automatically
      // We just return the response for the Register component to show verification message
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
  };

  // Update user profile using API
  const updateProfile = async (userData) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiClient.patch(config.AUTH_ENDPOINTS.UPDATE_PROFILE, userData);
      
      const { data } = response;
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      
      return data.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Update user data directly (used when user data is updated from profile component)
  const updateUserData = (userData) => {
    if (userData) {
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentUser(userData);
      return userData;
    }
    return null;
  };

  // Update password
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      setIsLoading(true);
      
      await apiClient.patch(config.AUTH_ENDPOINTS.UPDATE_PASSWORD, {
        currentPassword,
        newPassword
      });
      
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Update subscription
  const updateSubscription = async (subscriptionType) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiClient.patch(config.AUTH_ENDPOINTS.UPDATE_SUBSCRIPTION, {
        subscriptionType
      });
      
      const { data } = response;
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      
      return data.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Set JWT secret for SDK integration
  const setJwtSecret = (jwtSecret) => {
    // This method would be called by the SDK when integrating with client systems
    if (jwtSecret) {
      localStorage.setItem('jwtSecret', jwtSecret);
      // Update the API client headers
      apiClient.defaults.headers['X-JWT-Secret'] = jwtSecret;
    }
  };

  // Get current user on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      console.log('AUTH CONTEXT MOUNT DEBUG - Stored user:', storedUser);
      console.log('AUTH CONTEXT MOUNT DEBUG - Stored token:', token ? 'exists' : 'missing');
      
      if (storedUser && token) {
        const parsedUser = JSON.parse(storedUser);
        console.log('AUTH CONTEXT MOUNT DEBUG - Parsed user:', parsedUser);
        console.log('AUTH CONTEXT MOUNT DEBUG - isSubUser from storage:', parsedUser?.isSubUser);
        
        setCurrentUser(parsedUser);
        
        try {
          console.log('AUTH CONTEXT MOUNT DEBUG - Calling /api/auth/me...');
          // Verify token by fetching current user
          const response = await apiClient.get(config.AUTH_ENDPOINTS.ME);
          const { data } = response;
          
          console.log('AUTH CONTEXT MOUNT DEBUG - /api/auth/me response:', response);
          console.log('AUTH CONTEXT MOUNT DEBUG - Updated user from server:', data.user);
          console.log('AUTH CONTEXT MOUNT DEBUG - isSubUser from server:', data.user?.isSubUser);
          
          localStorage.setItem('user', JSON.stringify(data.user));
          setCurrentUser(data.user);
        } catch (error) {
          console.log('AUTH CONTEXT MOUNT DEBUG - /api/auth/me failed:', error);
          // If token is invalid, logout
          if (error.response && error.response.status === 401) {
            logout();
          }
        }
      }
      
      setIsLoading(false);
    };
    
    fetchCurrentUser();
  }, []);

  // Context value
  const value = {
    currentUser,
    isLoading,
    error,
    login,
    register,
    logout,
    updateProfile,
    updateUserData,
    updatePassword,
    updateSubscription,
    setJwtSecret,
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 